// src/data/adverseAuthority.js
//
// THE [VERIFY] GATE'S DATA. Hand-authored. There is no generator and no second copy.
//
// PRECISELY WHICH GATE, corrected 2026-08-28. The evidentiary standard's SS3 [VERIFY] guard is
// THREE things and this file is the data for ONE of them - the adverse-authority acknowledgement
// enforced at src\pages\ClaimNarrative.js (`gateBlocked`). It does NOT drive the OTHER half of
// SS3: refusing to print or approve a document that still carries a literal [VERIFY] marker. That
// half is NOT ENFORCED anywhere - DisciplineCases.js matches the marker and colours it AMBER, and
// amber does not survive a photocopier. Recorded at ISS-32; do not read this header as saying the
// print path is covered, because it is not.
//
// WHY THIS FILE IS THE SOURCE AND NOT A PROJECTION OF data\YCP_Precedent_Index.csv.
// UPDATED 2026-08-28: that index now holds 1,644 award rows of which EIGHT carry ChargeType,
// RuleCited, Defect and Outcome - the PLB 7303 awards, tagged from the documents as read. The other
// 1,636 are still a list of URLs, and they will stay that way for a while: the source PDFs are
// SCANNED IMAGES WITH NO TEXT LAYER, so nothing can be tagged by fetching. (Counted 0 of 1,636 on
// 2026-08-26; re-counted 8 of 1,644 on 2026-08-28. ISS-32.)
//
// So the original reason still holds - nothing in the index links an award to a RULE, and building
// this table off it would produce a check that never fires and a page that says it checked.
//
// BUT THE INDEX IS NOT USELESS TO THIS FILE, and as of 2026-08-28 it is wired in the other
// direction: verifier check AWD-1 reads THIS file and asserts every award cited here resolves in
// that index and is not marked SUSTAINED. The index cannot tell this table what to say. It can tell
// this table when it has said something the record does not support - which is the whole of SS3's
// second requirement, and it needed award NUMBERS, not award TEXT.
//
// So this table holds only what has actually been established, one entry at a time, each carrying
// where it came from and how far it has been read. It grows by adding a row.
//
// WHAT THE GATE MAY AND MAY NOT SAY.
//
// UPDATED 2026-08-28. The hold this header used to carry is DISCHARGED. The majority opinions of
// Awards 11 and 13 were read on 2026-08-27 (data\YCP_PLB7303_MAJORITY_OPINIONS_2026-08-27.md) and
// Awards 6-10 and 12 on 2026-08-28 (data\YCP_PLB7303_AWARDS_6_10_12_2026-08-28.md). Both readings
// were taken off the rendered page, not an OCR pass. The prior text of this header - that the
// majority opinions "have NOT been read" and that the platform's account comes from the dissent -
// was true when written and is now WITHDRAWN (VF-6).
//
// The gate still does NOT tell a member what to do. It tells him that authority EXISTS on this
// subject on this property, what the dispute was, what is established about its LIMITS, and where
// the document is. Warning a member that authority exists is not the same act as arguing his case,
// and ruling 27 bars only the second.
//
// WHAT IS STILL BARRED, AND IT IS NOT SMALL: the TEXT of BN Rules 3, 7B, 7E, 7F and 8K has never
// been read in the 1997 schedule. Every rule characterisation in this file is the BOARD'S, not the
// agreement's. PD-16 - no letter and no catalog row states what those rules say until they are read
// at their own line.
//
// SCOPE DISCIPLINE (PD-4 / no contamination). Every entry names the property it reaches. PLB 7303
// is a BN board - Burkart of GO-341 sat as Employee Member. It is NOT established as binding on
// Amtrak, Conrail YM, Conrail M/S, CSX or D&H, and `appliesTo` is what keeps it off them.


// Returns every adverse-authority entry that reaches this claim.
//
// Both tests must pass: the property must match, AND the subject must appear somewhere in what the
// claim is actually about. A claim with no violation type and no matched rule matches nothing -
// silence here means "nothing established", never "nothing exists". The gate's own text says so.
import { ADVERSE_AUTHORITY } from './adverseAuthorityTable';   // section 737: the table is installation content (ruling 321)
export { ADVERSE_AUTHORITY };

export function adverseFor({ railroad, agreement, matchedRules, violationType } = {}) {
  const property = [railroad, agreement].filter(Boolean).join(' ');
  if (!property) return [];
  const subjectText = [
    violationType || '',
    ...(Array.isArray(matchedRules) ? matchedRules.map(r => (r && (r.rule || r.label || r.violation_id)) || '') : []),
  ].join(' ');
  if (!subjectText.trim()) return [];
  return ADVERSE_AUTHORITY.filter(a => a.appliesTo.test(property) && a.subject.test(subjectText));
}

// How many linkages the platform actually holds. Shown in the gate so nobody reads a clean pass as
// "this claim was checked against the case law" - it was checked against two entries.
export const ADVERSE_AUTHORITY_COUNT = ADVERSE_AUTHORITY.length;
